import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '@/lib/money';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Users, Phone, MessageSquare, Mail, Save, Trash2, Loader2, ChevronRight,
} from 'lucide-react';
import {
  fetchMyChargerContacts,
  updateContactNote,
  removeContact,
  type MyChargerContact,
} from '@/lib/api';
import { toast } from '@/store/toast';
import formStyles from '@/components/Form.module.css';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './MyChargerContactsPage.module.css';

interface Props {
  chargerId: string;
  onNavigate: (path: string) => void;
}

const NOTE_MAX = 500;

/**
 * Pass-list contacts for a resident-owned charger. Owner sees one
 * card per user with tap-to-text / tap-to-call / tap-to-email and
 * an editable per-contact note.
 *
 * Empty state deep-links to the charger settings page's Free pass
 * list (?scroll=pass-list), where the owner adds people by entering
 * a COMPLETE email address or phone number — the old search-as-you-
 * type flow was removed 2026-08-05 (directory-enumeration leak).
 * Adding contacts isn't done from this page; the cards here are for
 * management of an already-curated list.
 */
export function MyChargerContactsPage({ chargerId, onNavigate }: Props) {
  const { t } = useTranslation('myChargersManage');
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-charger', chargerId, 'contacts'],
    queryFn: () => fetchMyChargerContacts(chargerId),
  });
  const contacts = data ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('back')}
          onClick={() => onNavigate(`/my-chargers/${chargerId}`)}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('contacts.title')}</h1>
          <p className={styles.subtitle}>
            {isLoading
              ? t('contacts.loading')
              : contacts.length === 0
                ? t('contacts.noContacts')
                : t('contacts.subtitle', { count: contacts.length })}
          </p>
        </div>
      </header>

      {isLoading && (
        <div className={styles.list}>
          <div className={styles.cardSkeleton} />
          <div className={styles.cardSkeleton} />
        </div>
      )}

      {!isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : t('contacts.loadError')}
        </div>
      )}

      {!isLoading && !error && contacts.length === 0 && (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>
            <NamedIcon name="contacts" fallback={<Users size={40} />} size={40} />
          </div>
          <h2 className={styles.emptyTitle}>{t('contacts.emptyTitle')}</h2>
          <p className={styles.emptyDesc}>
            {t('contacts.emptyDesc')}
          </p>
          <button
            type="button"
            className={styles.emptyCta}
            onClick={() => onNavigate(`/my-chargers/${chargerId}?scroll=pass-list`)}
          >
            {t('contacts.goToPassList')}
          </button>
        </div>
      )}

      {!isLoading && !error && contacts.length > 0 && (
        <div className={styles.list}>
          {contacts.map((c) => (
            <ContactCard
              key={c.user_id}
              chargerId={chargerId}
              contact={c}
              onOpen={() => onNavigate(`/my-chargers/${chargerId}/contacts/${c.user_id}`)}
              onNoteSaved={(userId, note) => {
                // Patch the cached contacts list in place so the
                // textarea retains its "saved" state without a full
                // refetch. The next background revalidation will
                // fetch the canonical row anyway.
                qc.setQueryData<MyChargerContact[] | undefined>(
                  ['my-charger', chargerId, 'contacts'],
                  (prev) => prev?.map((x) => x.user_id === userId
                    ? { ...x, note }
                    : x,
                  ),
                );
              }}
              onRemoved={(userId) => {
                qc.setQueryData<MyChargerContact[] | undefined>(
                  ['my-charger', chargerId, 'contacts'],
                  (prev) => prev?.filter((x) => x.user_id !== userId),
                );
                // The detail page caches the pass-list array too —
                // drop it so the next visit reflects the
                // post-removal shape. The DELETE response includes
                // the full charger doc; we could splice it in, but
                // an invalidate is simpler and the next visit
                // re-fetches anyway.
                qc.invalidateQueries({ queryKey: ['my-charger', chargerId] });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface ContactCardProps {
  chargerId: string;
  contact: MyChargerContact;
  onOpen: () => void;
  onNoteSaved: (userId: string, note: string | null) => void;
  onRemoved: (userId: string) => void;
}

function ContactCard({ chargerId, contact, onOpen, onNoteSaved, onRemoved }: ContactCardProps) {
  const { t, i18n } = useTranslation('myChargersManage');
  // What they paid, per currency (item 3): one amount, or each currency
  // they paid in side by side, never added up.
  const paidLabel = (contact.paid_by_currency && contact.paid_by_currency.length > 1
    ? contact.paid_by_currency.map((p) => formatMoney(p.cents, p.currency, i18n.language))
    : [formatMoney(contact.paid_cents, contact.currency, i18n.language)]
  ).join(' · ');
  const [noteDraft, setNoteDraft] = useState(contact.note ?? '');
  const [savingNote, setSavingNote] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const initials = computeInitials(contact.name ?? contact.email);
  const noteDirty = noteDraft.trim() !== (contact.note ?? '').trim();
  // Contact affordances (call/text/email, note, remove) exist only
  // for pass-list members — the server returns no phone/email/note
  // for paying guests and its note/remove routes 404 off-list, so
  // this gate mirrors the API rather than merely styling it.
  const isPassContact = contact.role === 'pass_list';
  const rolePillClass = contact.role === 'owner'
    ? styles.rolePillOwner
    : contact.role === 'pass_list'
      ? styles.rolePillPass
      : contact.role === 'visitor'
        ? styles.rolePillVisitor
        : styles.rolePillGuest;

  const handleSaveNote = async (): Promise<void> => {
    setSavingNote(true);
    try {
      const trimmed = noteDraft.trim();
      const next = trimmed === '' ? null : trimmed;
      await updateContactNote(chargerId, contact.user_id, next);
      onNoteSaved(contact.user_id, next);
      toast(t('contactCard.noteSavedToast'), { tone: 'accent' });
    } catch (err) {
      toast(
        err instanceof Error ? err.message : t('contactCard.noteSaveError'),
        { tone: 'error' },
      );
    } finally {
      setSavingNote(false);
    }
  };

  const handleRemove = async (): Promise<void> => {
    setRemoving(true);
    try {
      await removeContact(chargerId, contact.user_id);
      onRemoved(contact.user_id);
      toast(t('contactCard.removedToast'), { tone: 'neutral' });
    } catch (err) {
      toast(
        err instanceof Error ? err.message : t('contactCard.removeError'),
        { tone: 'error' },
      );
      setRemoving(false);
      setConfirmingRemove(false);
    }
  };

  return (
    <article className={styles.card}>
      {/* Header opens the per-contact history. A button (not a card-
          wide click target) so the note textarea, contact links and
          the remove flow below stay independently operable. */}
      <header
        className={styles.cardHeaderTappable}
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      >
        <span className={styles.avatar} aria-hidden>{initials}</span>
        <div className={styles.who}>
          <p className={styles.name}>
            {contact.name ?? t('contactCard.driverFallback')}
            <span className={rolePillClass}>{t(`contactCard.role.${contact.role}`)}</span>
          </p>
          {contact.email && (
            <p className={styles.secondary}>{contact.email}</p>
          )}
          {contact.added_at && (
            <p className={styles.tertiary}>{t('contactCard.added', { date: formatDate(new Date(contact.added_at)) })}</p>
          )}
        </div>
        <ChevronRight size={16} className={styles.cardChevron} aria-hidden />
      </header>

      {/* Usage at this charger — the "who is using my charger and
          how much" row. Zeros render as a single no-sessions line. */}
      <div className={styles.usageRow}>
        {contact.sessions === 0 ? (
          <span className={styles.usageStat}>
            {contact.role === 'visitor' && contact.last_seen_at
              ? t('contactCard.usage.visited', { date: formatDate(new Date(contact.last_seen_at)) })
              : t('contactCard.usage.never')}
          </span>
        ) : (
          <>
            <span className={styles.usageStat}>{t('contactCard.usage.sessions', { count: contact.sessions })}</span>
            <span className={styles.usageStat}>{t('contactCard.usage.kwh', { kwh: contact.kwh.toFixed(1) })}</span>
            {contact.last_charged_at && (
              <span className={styles.usageStat}>
                {t('contactCard.usage.last', { date: formatDate(new Date(contact.last_charged_at)) })}
              </span>
            )}
            {contact.role === 'paying_guest' && contact.paid_cents > 0 && (
              <span className={styles.usagePaid}>
                {t('contactCard.usage.paid', { amount: paidLabel })}
              </span>
            )}
          </>
        )}
      </div>

      {/* Action row — tel:/sms:/mailto: are standard browser deep
          links; mobile resolves to the system Phone / Messages /
          Mail apps. Desktop may prompt or no-op (PWA primary
          surface is mobile, so that's expected). Pass-list only:
          the server returns no phone/email for other roles. */}
      {isPassContact && (
      <div className={styles.actions}>
        {contact.phone ? (
          <>
            <a
              className={styles.actionBtn}
              href={`tel:${contact.phone}`}
              aria-label={t('contactCard.callAria', { name: contact.name ?? t('contactCard.contactFallback') })}
            >
              <Phone size={14} />
              <span className={styles.actionLabel}>{t('contactCard.call')}</span>
            </a>
            <a
              className={styles.actionBtn}
              href={`sms:${contact.phone}`}
              aria-label={t('contactCard.textAria', { name: contact.name ?? t('contactCard.contactFallback') })}
            >
              <MessageSquare size={14} />
              <span className={styles.actionLabel}>{t('contactCard.text')}</span>
            </a>
          </>
        ) : (
          <span className={styles.noPhone}>{t('contactCard.noPhone')}</span>
        )}
        {contact.email && (
          <a
            className={styles.actionBtn}
            href={`mailto:${contact.email}`}
            aria-label={t('contactCard.emailAria', { name: contact.name ?? t('contactCard.contactFallback') })}
          >
            <Mail size={14} />
            <span className={styles.actionLabel}>{t('contactCard.email')}</span>
          </a>
        )}
      </div>
      )}

      {/* Note editor — pass-list only; the PATCH route 404s for
          anyone not on the list. */}
      {isPassContact && (
      <div className={styles.noteBlock}>
        <label htmlFor={`note-${contact.user_id}`} className={styles.noteLabel}>
          {t('contactCard.noteLabel')}
        </label>
        <textarea
          id={`note-${contact.user_id}`}
          className={`${formStyles.input} ${styles.noteInput}`}
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value.slice(0, NOTE_MAX))}
          placeholder={t('contactCard.notePlaceholder')}
          maxLength={NOTE_MAX}
          rows={3}
          disabled={savingNote || removing}
        />
        <div className={styles.noteFooter}>
          <span className={styles.charCount}>
            {noteDraft.length} / {NOTE_MAX}
          </span>
          <button
            type="button"
            className={formStyles.btnPrimary}
            disabled={!noteDirty || savingNote || removing}
            onClick={handleSaveNote}
          >
            {savingNote
              ? <><Loader2 size={13} className={styles.spin} /> {t('contactCard.saving')}</>
              : <><Save size={13} /> {t('contactCard.saveNote')}</>}
          </button>
        </div>
      </div>
      )}

      {/* Remove from pass list — two-tap confirm avoids accidental
          taps on a destructive action. The confirm chip stays for
          ~5s before reverting if the resident doesn't follow through.
          Pass-list only: there is nothing to remove a paying guest
          or the owner FROM. */}
      {isPassContact && (
      <div className={styles.removeBlock}>
        {!confirmingRemove ? (
          <button
            type="button"
            className={styles.removeBtn}
            onClick={() => setConfirmingRemove(true)}
            disabled={removing}
          >
            <Trash2 size={13} /> {t('contactCard.removeFromList')}
          </button>
        ) : (
          <div className={styles.confirmRow}>
            <span className={styles.confirmText}>
              {t('contactCard.removeConfirm', { name: contact.name ?? t('contactCard.thisContact') })}
            </span>
            <button
              type="button"
              className={styles.confirmCancel}
              onClick={() => setConfirmingRemove(false)}
              disabled={removing}
            >
              {t('contactCard.cancel')}
            </button>
            <button
              type="button"
              className={styles.confirmYes}
              onClick={handleRemove}
              disabled={removing}
            >
              {removing
                ? <><Loader2 size={12} className={styles.spin} /> {t('contactCard.removing')}</>
                : t('contactCard.yesRemove')}
            </button>
          </div>
        )}
      </div>
      )}
    </article>
  );
}

/** Two-letter initials from name (or email local-part fallback). */
function computeInitials(input: string | null): string {
  if (!input) return '·';
  const trimmed = input.trim();
  if (trimmed.includes('@')) {
    return trimmed.slice(0, 2).toUpperCase();
  }
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

function formatDate(d: Date): string {
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
